import { For, Show, createMemo, createSignal, type Component } from 'solid-js';

import {
  CMD_TEST_GRANT_XP,
  CMD_TEST_REFILL_BELT,
  CMD_TEST_SET_LEVEL,
  CMD_TEST_SPAWN_ITEM,
  testGrantXP,
  testRefillBelt,
  testSetLevel,
  testSpawnItem,
  type ItemRarity,
  type RefusalReason,
} from '../net/protocol';
import type { GameStore } from '../state/store';
import { KEYBINDS } from '../game/keyboard';

/**
 * The developer panel, from Phase 1 of `doc/grind/ideas/00-ROADMAP.md`.
 *
 * Four things a build needs to be testable at all: conjure a piece of gear, reach a
 * level, earn experience, and fill the belt without walking home. Every one of them is
 * a `CMD_TEST_*` the **server** refuses unless the active league permits it — this
 * component sends and displays, and decides nothing.
 *
 * ── Why it is not in `PANELS` ────────────────────────────────────────────────
 *
 * Every entry in `game/keyboard.ts`'s `PANELS` must open unconditionally, because a
 * walking test presses each advertised key and the HUD bar prints them all. This panel
 * exists only when the server says test mode is on, so it is routed by hand and its
 * affordance is `TestModeBar` below — visible only where it works, exactly as
 * `StationBar` is only in town.
 *
 * ── Why the catalogue comes from the server ──────────────────────────────────
 *
 * `store.testMode()` carries every base and unique id with the item level it starts
 * dropping at. A hardcoded list here would drift the moment a base was added, and a
 * free-text field would be asking a developer to recall `items.yaml` from memory — and
 * both would be inventing data the server owns. The panel can therefore say "that base
 * does not drop until item level 40" before sending anything, while still leaving the
 * *decision* where it belongs: the server re-checks all of it and words its own answer.
 */

/** The four rarities, in the catalogue's own ascending order. */
const RARITIES: readonly ItemRarity[] = ['NORMAL', 'MAGIC', 'RARE', 'UNIQUE'];

/**
 * Wording for a refused developer command.
 *
 * Separate from `refusalText`, `beltRefusalText` and `dodgeRefusalText` in
 * `ui/actionbar.ts` for the reason those three are separate from each other: `LEVEL`
 * and `COOLDOWN` are keys that mean different things in different vocabularies, and
 * these sentences belong beside the field they are about rather than in the action
 * bar's corner of the screen.
 */
export function testRefusalText(reason: RefusalReason): string {
  switch (reason) {
    case 'NOT_PERMITTED':
      return 'This league does not permit developer commands.';
    case 'UNKNOWN_BASE':
      return 'No base item or unique with that id.';
    case 'ITEM_LEVEL':
      return 'That does not drop at this item level.';
    case 'RARITY':
      return 'A unique can only be spawned at UNIQUE, and nothing else can.';
    case 'BAG_FULL':
      return 'The bag is full.';
    case 'UNREACHABLE':
      return 'The generator could not roll that combination.';
    case 'LEVEL':
      return 'Levels only go up, and only to the cap.';
    case 'AMOUNT':
      return 'Ask for a positive amount.';
    default:
      // Every other key belongs to another vocabulary, and inventing a sentence for it
      // would be worse than showing the key: at least the key can be looked up.
      return reason;
  }
}

/**
 * The affordance: a small badge, top-left, present only when the server said test mode
 * is on.
 *
 * Top-left because that corner is empty. Everything else interactive is placed
 * elsewhere — `.grind-portal` and `.stations` at the top centre, `.action-bar` at the
 * bottom centre, `.hud-menu` bottom-right — and an interactive box that overlapped one
 * of those would swallow its clicks, which is how a 599 × 114 action bar once became a
 * 485 × 221 block. Measured in a real browser, because jsdom computes no layout.
 */
export const TestModeBar: Component<{ store: GameStore }> = (props) => (
  <Show when={props.store.testMode()}>
    <div class="testmode hud-interactive" data-testid="testmode-bar">
      <button
        type="button"
        class="testmode__button"
        classList={{ 'testmode__button--open': props.store.menu() === 'test' }}
        data-testid="testmode-open"
        aria-pressed={props.store.menu() === 'test'}
        title={`Developer panel (${KEYBINDS.test}) — this league permits developer commands`}
        onClick={() => props.store.toggleMenu('test')}
      >
        <span class="testmode__sigil" aria-hidden="true">
          ⚙
        </span>
        <span class="testmode__label">Test mode</span>
        <span class="testmode__key">{KEYBINDS.test}</span>
      </button>
    </div>
  </Show>
);

/** The panel itself. */
export const TestPanel: Component<{ store: GameStore }> = (props) => {
  const info = () => props.store.testMode();
  const permits = (command: string) => info()?.commands.includes(command) ?? false;

  const [query, setQuery] = createSignal('');
  const [baseId, setBaseId] = createSignal('');
  const [rarity, setRarity] = createSignal<ItemRarity>('RARE');
  const [itemLevel, setItemLevel] = createSignal(1);
  const [level, setLevel] = createSignal(2);
  const [amount, setAmount] = createSignal(1000);

  /**
   * The catalogue, filtered by the search box and capped.
   *
   * Capped because the list is 132 entries and a panel that renders all of them is a
   * scroll rather than a chooser — the search box is the way to the rest.
   */
  const matches = createMemo(() => {
    const needle = query().trim().toLowerCase();
    const all = info()?.bases ?? [];
    const hits = needle
      ? all.filter(
          (b) => b.baseId.toLowerCase().includes(needle) || b.name.toLowerCase().includes(needle),
        )
      : all;
    return hits.slice(0, 40);
  });

  /** The chosen entry, or undefined while nothing is chosen. */
  const chosen = createMemo(() => info()?.bases.find((b) => b.baseId === baseId()));

  /**
   * What the *client* can already tell about a spawn, in the server's own words.
   *
   * It does not gate the button — the press has to reach the server so the answer can
   * come back, the rule an empty action-bar slot follows — it just says the thing the
   * client already knows without a round trip. The server sends the same key for the
   * same request, and its answer overwrites this.
   */
  const spawnWarning = createMemo<RefusalReason | null>(() => {
    const entry = chosen();
    if (!entry) return baseId().trim() === '' ? null : 'UNKNOWN_BASE';
    // `Boolean`, because `unique` is `omitempty` on the wire: an ordinary base arrives
    // with the key absent, and `undefined !== false` is true. Without this every
    // ordinary base warned about its rarity, which is a warning that cries wolf on the
    // common case and would train a developer to ignore the one that matters.
    if (Boolean(entry.unique) !== (rarity() === 'UNIQUE')) return 'RARITY';
    if (itemLevel() < entry.minLevel) return 'ITEM_LEVEL';
    return null;
  });

  const send = (cmd: Parameters<GameStore['send']>[0]) => {
    props.store.clearTestNotice();
    props.store.send(cmd);
  };

  return (
    <Show when={props.store.menu() === 'test' && info()}>
      {(mode) => (
        <div
          class="sheet hud-interactive"
          data-testid="test-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Developer panel"
        >
          <div class="plate plate--riveted sheet__panel">
            <header class="sheet__head">
              <h2 class="sheet__title">Test Mode</h2>
              <button
                type="button"
                class="sheet__close"
                data-testid="test-close"
                onClick={() => props.store.closeMenu()}
                aria-label="Close"
              >
                ✕
              </button>
            </header>

            {/* What this is and what it is not. The panel exists because the league
                says so, and every button here is answered by the server. */}
            <p class="sheet__seam" data-testid="test-preamble">
              This league permits developer commands. Every one of them is checked
              server-side — the panel asks, the server decides.
            </p>

            <Show when={props.store.testNotice()}>
              {(reason) => (
                <p class="testpanel__refusal" data-testid="test-refusal" role="status">
                  {testRefusalText(reason())}
                </p>
              )}
            </Show>

            {/* ── Spawn an item ──────────────────────────────────────────── */}
            <section class="testpanel__section" aria-label="Spawn an item">
              <h3 class="sheet__heading">Spawn an item</h3>

              <Show
                when={permits(CMD_TEST_SPAWN_ITEM)}
                fallback={
                  <p class="sheet__seam" data-testid="test-spawn-locked">
                    This league does not permit item spawning.
                  </p>
                }
              >
                <p class="sheet__seam">
                  Rolled by the real loot generator, so a spawned item is one the game
                  could have dropped.
                </p>

                <input
                  type="search"
                  class="testpanel__input"
                  data-testid="test-search"
                  placeholder="Search bases and uniques"
                  value={query()}
                  onInput={(e) => setQuery(e.currentTarget.value)}
                  aria-label="Search the catalogue"
                />

                <ul class="testpanel__list" data-testid="test-base-list">
                  <For each={matches()}>
                    {(base) => (
                      <li>
                        <button
                          type="button"
                          class="testpanel__base"
                          classList={{ 'testpanel__base--chosen': base.baseId === baseId() }}
                          data-testid={`test-base-${base.baseId}`}
                          aria-pressed={base.baseId === baseId()}
                          onClick={() => {
                            setBaseId(base.baseId);
                            // A unique can only be rolled at UNIQUE and nothing else
                            // can, so choosing one settles the rarity rather than
                            // leaving a request the server would refuse.
                            setRarity(base.unique ? 'UNIQUE' : 'RARE');
                            // Its own floor, because below it the generator cannot
                            // produce the item at all.
                            setItemLevel((current) => Math.max(current, base.minLevel));
                          }}
                        >
                          <span class="testpanel__base-name">{base.name}</span>
                          <span class="testpanel__base-meta">
                            {base.slot} · i{base.minLevel}
                            {base.unique ? ' · unique' : ''}
                          </span>
                        </button>
                      </li>
                    )}
                  </For>
                </ul>

                <div class="testpanel__row">
                  <label class="testpanel__field">
                    <span>Rarity</span>
                    <select
                      class="testpanel__input"
                      data-testid="test-rarity"
                      value={rarity()}
                      onChange={(e) => setRarity(e.currentTarget.value as ItemRarity)}
                    >
                      <For each={RARITIES}>{(r) => <option value={r}>{r}</option>}</For>
                    </select>
                  </label>

                  <label class="testpanel__field">
                    <span>Item level</span>
                    <input
                      type="number"
                      class="testpanel__input"
                      data-testid="test-item-level"
                      min="1"
                      value={itemLevel()}
                      onInput={(e) => setItemLevel(Number(e.currentTarget.value))}
                    />
                  </label>

                  {/* Never `disabled`: the press has to reach the server so the answer
                      can come back, which is the rule an empty action-bar slot follows. */}
                  <button
                    type="button"
                    class="testpanel__action"
                    data-testid="test-spawn"
                    onClick={() => send(testSpawnItem(baseId(), rarity(), itemLevel()))}
                  >
                    Spawn
                  </button>
                </div>

                <Show when={spawnWarning()}>
                  {(reason) => (
                    <p class="testpanel__warning" data-testid="test-spawn-warning">
                      {testRefusalText(reason())}
                    </p>
                  )}
                </Show>
              </Show>
            </section>

            {/* ── Progression ────────────────────────────────────────────── */}
            <section class="testpanel__section" aria-label="Progression">
              <h3 class="sheet__heading">Progression</h3>

              <Show
                when={permits(CMD_TEST_SET_LEVEL) || permits(CMD_TEST_GRANT_XP)}
                fallback={
                  <p class="sheet__seam" data-testid="test-progression-locked">
                    This league does not permit progression commands.
                  </p>
                }
              >
                <p class="sheet__seam">
                  A level is granted as experience, so the points come from levelling and
                  the allocation invariants hold. Levels only go up.
                </p>

                <div class="testpanel__row">
                  <Show when={permits(CMD_TEST_SET_LEVEL)}>
                    <label class="testpanel__field">
                      <span>Level (cap {mode().maxLevel})</span>
                      <input
                        type="number"
                        class="testpanel__input"
                        data-testid="test-level"
                        min="2"
                        max={mode().maxLevel}
                        value={level()}
                        onInput={(e) => setLevel(Number(e.currentTarget.value))}
                      />
                    </label>
                    <button
                      type="button"
                      class="testpanel__action"
                      data-testid="test-set-level"
                      onClick={() => send(testSetLevel(level()))}
                    >
                      Set level
                    </button>
                  </Show>

                  <Show when={permits(CMD_TEST_GRANT_XP)}>
                    <label class="testpanel__field">
                      <span>Experience</span>
                      <input
                        type="number"
                        class="testpanel__input"
                        data-testid="test-amount"
                        min="1"
                        value={amount()}
                        onInput={(e) => setAmount(Number(e.currentTarget.value))}
                      />
                    </label>
                    <button
                      type="button"
                      class="testpanel__action"
                      data-testid="test-grant-xp"
                      onClick={() => send(testGrantXP(amount()))}
                    >
                      Grant XP
                    </button>
                  </Show>
                </div>
              </Show>
            </section>

            {/* ── The belt ───────────────────────────────────────────────── */}
            <section class="testpanel__section" aria-label="Potion belt">
              <h3 class="sheet__heading">Potion belt</h3>

              <Show
                when={permits(CMD_TEST_REFILL_BELT)}
                fallback={
                  <p class="sheet__seam" data-testid="test-belt-locked">
                    This league does not permit refilling the belt.
                  </p>
                }
              >
                <p class="sheet__seam">
                  Only town restocks a belt. This suspends that, and the server still
                  decides the capacity.
                </p>
                <button
                  type="button"
                  class="testpanel__action"
                  data-testid="test-refill"
                  onClick={() => send(testRefillBelt())}
                >
                  Refill belt
                </button>
              </Show>
            </section>
          </div>
        </div>
      )}
    </Show>
  );
};
